import React, { useMemo } from 'react';
import Grid from 'apollo-react/components/Grid';
import TextField from 'apollo-react/components/TextField';
import Select from 'apollo-react/components/Select';
import MenuItem from 'apollo-react/components/MenuItem';
import DatePicker from 'apollo-react/components/DatePicker';
import Button from 'apollo-react/components/Button';
import { toast } from 'react-toastify';
import ProfileAttachmentBlock, {
  AttachmentSlotRevisionHistory,
} from '../components/ProfileAttachmentBlock';
import RepeatableSectionList from '../components/RepeatableSectionList';
import {
  licenseFieldConfigDefaults,
  addressCountryOptions,
  hcpProfileFormFieldVariant,
  hcpProfileDatePickerTextFieldProps,
  hcpProfileTextFieldProps,
  normalizeAddressCountryValue,
  mergeOptionIfMissing,
  labelForAddressCountry,
} from '../../../components/HcpSignup/hcpSignupDefaults';
import {
  HCP_SIGNUP_FIELD_HINTS,
  HcpSignupLabelWithHint,
} from '../../../components/HcpSignup/HcpSignupFieldLabel';
import { buildLicenseItemSchema } from '../../../components/HcpSignup/licensesSchema';
import {
  postHcpProfileLicense,
  putHcpProfileLicense,
  deleteHcpProfileLicense,
} from '../hcpProfileApi';
import profileStyles from '../../customerProfile/profileUi/Profile.module.css';
import buttonStyles from '../../../components/common/Button.module.css';
import { ProfileWizardSingleFileField } from '../components/profileWizardFileFields';
import {
  datePickerValueToIsoDate,
  isoDateToDatePickerValue,
} from '../apolloDateUtils';

/** @param {string} text @param {keyof typeof HCP_SIGNUP_FIELD_HINTS} hintKey */
function licenseFieldLabel(text, hintKey) {
  return (
    <HcpSignupLabelWithHint
      text={text}
      hint={HCP_SIGNUP_FIELD_HINTS[hintKey]}
    />
  );
}

function mapFromApi(lic) {
  return {
    licenseRowId: lic.licenseRowId,
    prefix: lic.prefix || '',
    licenseNumber: lic.licenseNumber || '',
    issuingCountry: normalizeAddressCountryValue(lic.issuingCountry || ''),
    authorityName: lic.authorityName || '',
    issueDate: lic.issueDate ? String(lic.issueDate).slice(0, 10) : '',
    expiryDate: lic.expiryDate ? String(lic.expiryDate).slice(0, 10) : '',
    existingAttachmentUploadId: lic.attachment?.id ?? null,
    attachment: lic.attachment,
  };
}

function emptyDraft() {
  return {
    prefix: '',
    licenseNumber: '',
    issuingCountry: '',
    authorityName: '',
    issueDate: '',
    expiryDate: '',
    existingAttachmentUploadId: null,
    attachmentFile: null,
  };
}

function rowToDraft(r) {
  return {
    prefix: r.prefix || '',
    licenseNumber: r.licenseNumber || '',
    issuingCountry: r.issuingCountry || '',
    authorityName: r.authorityName || '',
    issueDate: r.issueDate || '',
    expiryDate: r.expiryDate || '',
    existingAttachmentUploadId:
      r.existingAttachmentUploadId ?? r.attachment?.id ?? null,
    attachmentFile: null,
  };
}

export default function LicenseInformationTab({
  licenses,
  licenseFieldConfig,
  licenseRepeatableLimits,
  attachmentRevisions = [],
  onSaved,
  /** When true, list is view-only (e.g. attached profile snapshot for reviewers). */
  readOnly = false,
  /** When false, hide superseded / prior-version attachment UI for this tab. */
  showAttachmentPriorVersions = true,
  /** Admin request review: hide Add / Edit / Delete chrome on repeatable rows. */
  suppressApplicantActionChrome = false,
}) {
  const mergedCfg = useMemo(
    () => ({ ...licenseFieldConfigDefaults, ...(licenseFieldConfig || {}) }),
    [licenseFieldConfig]
  );
  const itemSchema = useMemo(
    () => buildLicenseItemSchema(mergedCfg),
    [mergedCfg]
  );

  const rows = useMemo(
    () => (Array.isArray(licenses) ? licenses.map(mapFromApi) : []),
    [licenses]
  );

  const minEntries = licenseRepeatableLimits?.minEntries ?? 0;
  const maxEntries =
    licenseRepeatableLimits?.maxEntries == null
      ? null
      : licenseRepeatableLimits.maxEntries;

  return (
    <RepeatableSectionList
      items={rows}
      getRowId={(r) => r.licenseRowId}
      locked={readOnly}
      showAddButton={!suppressApplicantActionChrome}
      minEntries={minEntries}
      maxEntries={maxEntries}
      title="License Information"
      titleVariant="section"
      addButtonLabel="Add license"
      emptyMessage="No licenses on file."
      editorTitleNew="New license"
      editorTitleEdit="Edit license"
      deleteModalTitle="Remove license"
      createEmptyDraft={emptyDraft}
      rowToDraft={rowToDraft}
      validateDraft={async (draft) => {
        await itemSchema.validate(
          { _rowKey: 'edit', ...draft },
          { abortEarly: false }
        );
      }}
      saveDraft={async (draft, { isNew, editingId }) => {
        const fields = {
          prefix: draft.prefix,
          licenseNumber: draft.licenseNumber,
          issuingCountry: draft.issuingCountry,
          authorityName: draft.authorityName,
          issueDate: draft.issueDate,
          expiryDate: draft.expiryDate,
        };
        if (isNew) {
          await postHcpProfileLicense(fields, draft.attachmentFile);
          toast.success('License added');
        } else {
          await putHcpProfileLicense(editingId, fields, draft.attachmentFile);
          toast.success('License updated');
        }
      }}
      deleteItem={async (rowId) => {
        await deleteHcpProfileLicense(rowId);
        toast.success('License removed');
      }}
      getDeleteConfirmLabel={(r) => r.licenseNumber || String(r.licenseRowId)}
      onSaved={onSaved}
      renderEditor={({
        draft: d,
        updateDraft,
        isNew,
        editingId,
        rows: editorRows,
        saving,
        fieldErrors: fe = {},
      }) => {
        const prefixReq =
          mergedCfg.prefixRequired === true ||
          mergedCfg.prefixOptional === false;
        const issuingCountryOptions = mergeOptionIfMissing(
          addressCountryOptions,
          d.issuingCountry
        );
        const defaultLicenseFileHint = isNew
          ? 'Upload is required.'
          : 'Upload a file to replace the current attachment.';
        return (
          <Grid
            container
            rowSpacing={0}
            columnSpacing={2}
            className={`mb-3 ${profileStyles.wizardFormGrid}`}
          >
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                {...hcpProfileTextFieldProps}
                label={licenseFieldLabel(
                  prefixReq ? 'Prefix' : 'Prefix (optional)',
                  'licensePrefix'
                )}
                value={d.prefix || ''}
                disabled={saving}
                onChange={(e) => updateDraft('prefix', e.target.value)}
                error={Boolean(fe.prefix)}
                helperText={fe.prefix || ''}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                {...hcpProfileTextFieldProps}
                label={licenseFieldLabel('License number', 'licenseNumber')}
                value={d.licenseNumber}
                disabled={saving}
                onChange={(e) => updateDraft('licenseNumber', e.target.value)}
                error={Boolean(fe.licenseNumber)}
                helperText={fe.licenseNumber || ''}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <Select
                label="Issuing Country"
                fullWidth
                variant={hcpProfileFormFieldVariant}
                value={d.issuingCountry}
                disabled={saving}
                onChange={(e) => updateDraft('issuingCountry', e.target.value)}
                error={Boolean(fe.issuingCountry)}
                helperText={fe.issuingCountry || ''}
              >
                <MenuItem value="" disabled>
                  <em>Select country</em>
                </MenuItem>
                {issuingCountryOptions.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                {...hcpProfileTextFieldProps}
                label={licenseFieldLabel(
                  'Authority name',
                  'licenseAuthorityName'
                )}
                value={d.authorityName}
                disabled={saving}
                onChange={(e) => updateDraft('authorityName', e.target.value)}
                error={Boolean(fe.authorityName)}
                helperText={fe.authorityName || ''}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <DatePicker
                label={licenseFieldLabel('Issue date', 'licenseIssueDate')}
                fullWidth
                disabled={saving}
                value={isoDateToDatePickerValue(d.issueDate)}
                onChange={(v) =>
                  updateDraft('issueDate', datePickerValueToIsoDate(v))
                }
                TextFieldProps={{
                  ...hcpProfileDatePickerTextFieldProps,
                  error: Boolean(fe.issueDate),
                  helperText: fe.issueDate || '',
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <DatePicker
                label={licenseFieldLabel('Expiry date', 'licenseExpiryDate')}
                fullWidth
                disabled={saving}
                value={isoDateToDatePickerValue(d.expiryDate)}
                onChange={(v) =>
                  updateDraft('expiryDate', datePickerValueToIsoDate(v))
                }
                TextFieldProps={{
                  ...hcpProfileDatePickerTextFieldProps,
                  error: Boolean(fe.expiryDate),
                  helperText: fe.expiryDate || '',
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <div className={profileStyles.attachmentPanel}>
                <ProfileWizardSingleFileField
                  id={`hcp_profile_license_attachment_${editingId ?? 'new'}`}
                  label="Copy of current / previous license"
                  localFile={d.attachmentFile}
                  existingServerFile={
                    !isNew && d.existingAttachmentUploadId
                      ? editorRows.find((x) => x.licenseRowId === editingId)
                          ?.attachment || {
                          id: d.existingAttachmentUploadId,
                          originalName: 'Current attachment',
                        }
                      : null
                  }
                  onFileChange={(f) => {
                    updateDraft('attachmentFile', f);
                    if (f) updateDraft('existingAttachmentUploadId', null);
                  }}
                  onClearExistingServerFile={() =>
                    updateDraft('existingAttachmentUploadId', null)
                  }
                  disabled={saving}
                  preset="attachment"
                  requiredHint={isNew && mergedCfg.licenseAttachmentRequired}
                  showError={Boolean(fe.attachmentFile)}
                  helperText={fe.attachmentFile || defaultLicenseFileHint}
                />
                {typeof editingId === 'number' &&
                showAttachmentPriorVersions ? (
                  <AttachmentSlotRevisionHistory
                    slotKey={`license:${editingId}`}
                    attachmentRevisions={attachmentRevisions}
                    title="Prior license uploads"
                  />
                ) : null}
              </div>
            </Grid>
          </Grid>
        );
      }}
      renderRow={({
        item: r,
        index: idx,
        canDelete,
        globalLock,
        deleteLocked,
        onEdit,
        onRequestDelete,
      }) => (
        <div className={profileStyles.nestedCard}>
          <div className={profileStyles.recordBadge}>
            License record #{idx + 1}
          </div>
          <div className={profileStyles.row}>
            <span className={profileStyles.label}>Numeric code:</span>
            <span className={profileStyles.value}>
              {[r.prefix, r.licenseNumber].filter(Boolean).join(' ') ||
                r.licenseNumber ||
                '—'}
            </span>
          </div>
          <div className={profileStyles.row}>
            <span className={profileStyles.label}>Issuing country:</span>
            <span className={profileStyles.value}>
              {labelForAddressCountry(r.issuingCountry)}
            </span>
          </div>
          <div className={profileStyles.row}>
            <span className={profileStyles.label}>Authority name:</span>
            <span className={profileStyles.value}>
              {r.authorityName || '—'}
            </span>
          </div>
          <div className={profileStyles.row}>
            <span className={profileStyles.label}>Issue date:</span>
            <span className={profileStyles.value}>{r.issueDate || '—'}</span>
          </div>
          <div className={profileStyles.row}>
            <span className={profileStyles.label}>Expiry date:</span>
            <span className={profileStyles.value}>{r.expiryDate || '—'}</span>
          </div>
          <div className={profileStyles.row}>
            <span className={profileStyles.label}>License document:</span>
            <div className={profileStyles.valueColumn}>
              <div className={profileStyles.attachmentPanel}>
                <ProfileAttachmentBlock
                  file={r.attachment}
                  slotKey={`license:${r.licenseRowId}`}
                  attachmentRevisions={attachmentRevisions}
                  showPriorVersions={showAttachmentPriorVersions}
                />
              </div>
            </div>
          </div>
          {!suppressApplicantActionChrome ? (
            <div className={profileStyles.inlineActions}>
              <Button
                variant="secondary"
                size="small"
                className={buttonStyles.button}
                disabled={globalLock}
                onClick={onEdit}
              >
                Edit
              </Button>
              <Button
                variant="primary"
                size="small"
                className={buttonStyles.button}
                disabled={globalLock || deleteLocked || !canDelete}
                onClick={onRequestDelete}
              >
                Delete
              </Button>
            </div>
          ) : null}
        </div>
      )}
    />
  );
}
